const blobUrlRegistry = new Map();
let isDbInitialized = false;
const DB_NAME = "BMM_IMAGE_CACHE_DB";
const STORE_NAME = "uploaded_images";
const DB_VERSION = 1;

export const LOCAL_FALLBACK_IMAGES = {
  campus: "/assets/images/uploads/madrasah_campus_hero_1790844614157.jpg",
  classroom: "/assets/images/uploads/madrasah_classroom_study_1790844625777.jpg",
  curriculum: "/assets/images/uploads/madrasah_curriculum_books_1790844639075.jpg",
  books: "/assets/images/uploads/madrasah_curriculum_books_1790844639075.jpg",
  teachers: "/assets/images/uploads/madrasah_teachers_campus_1790844650496.jpg",
  default: "/assets/images/uploads/madrasah_campus_hero_1790844614157.jpg"
};

export const PRESET_UPLOAD_PHOTOS = [
  {
    name: "Campus Exterior & Minaret",
    path: "/assets/images/uploads/madrasah_campus_hero_1790844614157.jpg",
    category: "campus"
  },
  {
    name: "Classroom & Students Recitation",
    path: "/assets/images/uploads/madrasah_classroom_study_1790844625777.jpg",
    category: "classroom"
  },
  {
    name: "Integrated Books & Calligraphy Slates",
    path: "/assets/images/uploads/madrasah_curriculum_books_1790844639075.jpg",
    category: "curriculum"
  },
  {
    name: "Faculty & Scholars Campus",
    path: "/assets/images/uploads/madrasah_teachers_campus_1790844650496.jpg",
    category: "teachers"
  }
];

export const PRESET_TEACHER_PHOTOS = [
  {
    name: "Principal / Senior Scholar",
    path: "/assets/images/uploads/madrasah_teachers_campus_1790844650496.jpg"
  },
  {
    name: "Vice Principal / Hadith Scholar",
    path: "/assets/images/uploads/madrasah_teachers_campus_1790844650496.jpg"
  },
  {
    name: "Noorani & Tajweed Head",
    path: "/assets/images/uploads/madrasah_classroom_study_1790844625777.jpg"
  },
  {
    name: "Academic Master (B.Sc)",
    path: "/assets/images/uploads/madrasah_classroom_study_1790844625777.jpg"
  },
  {
    name: "Arabic & Farsi Teacher",
    path: "/assets/images/uploads/madrasah_curriculum_books_1790844639075.jpg"
  },
  {
    name: "Hifz Boarding In-Charge",
    path: "/assets/images/uploads/madrasah_campus_hero_1790844614157.jpg"
  }
];

export const DEFAULT_FALLBACK_IMAGE = LOCAL_FALLBACK_IMAGES.campus;

function openImageDatabase() {
  return new Promise((resolve, reject) => {
    if (typeof window === "undefined" || !window.indexedDB) {
      reject(new Error("IndexedDB not supported"));
      return;
    }
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: "path" });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

export async function initImageStorage() {
  if (isDbInitialized || typeof window === "undefined") return;
  try {
    const db = await openImageDatabase();
    const tx = db.transaction(STORE_NAME, "readonly");
    const store = tx.objectStore(STORE_NAME);
    const getAllRequest = store.getAll();
    getAllRequest.onsuccess = () => {
      const records = getAllRequest.result || [];
      records.forEach((item) => {
        try {
          let blob;
          if (item.blob instanceof Blob) {
            blob = item.blob;
          } else if (item.blob) {
            blob = new Blob([item.blob], { type: item.mimeType || "image/jpeg" });
          }
          if (blob) {
            const objectUrl = URL.createObjectURL(blob);
            blobUrlRegistry.set(item.path, objectUrl);
          }
        } catch (err) {
          console.warn("Could not restore image blob for path:", item.path, err);
        }
      });
      isDbInitialized = true;
      window.dispatchEvent(new CustomEvent("bmm-images-ready"));
    };
  } catch (e) {
    console.warn("IndexedDB image storage could not be initialized:", e);
    isDbInitialized = true;
  }
}

export function sanitizeFileName(originalName) {
  const parts = originalName.split(".");
  const ext = parts.length > 1 ? parts.pop()?.toLowerCase() || "jpg" : "jpg";
  const baseName = parts.join("_").toLowerCase().replace(/[^a-z0-9_-]/g, "_").replace(/_+/g, "_").substring(0, 32);
  return `${baseName || "image"}_${Date.now()}.${ext}`;
}

export function saveImageToUploadFolder(file, folder = "pages", subCategory) {
  const cleanName = sanitizeFileName(file.name);
  const targetFolder = subCategory ? `/assets/images/uploads/${folder}/${subCategory}` : `/assets/images/uploads/${folder}`;
  const cleanPath = `${targetFolder}/${cleanName}`;
  try {
    const objectUrl = URL.createObjectURL(file);
    blobUrlRegistry.set(cleanPath, objectUrl);
  } catch (e) {
    console.warn("Could not register blob URL:", e);
  }

  // Persist locally in IndexedDB
  if (typeof window !== "undefined" && window.indexedDB) {
    openImageDatabase().then((db) => {
      const tx = db.transaction(STORE_NAME, "readwrite");
      const store = tx.objectStore(STORE_NAME);
      store.put({
        path: cleanPath,
        blob: file,
        mimeType: file.type || "image/jpeg",
        name: file.name,
        updated_at: Date.now()
      });
    }).catch((err) => console.warn("Could not save image to IndexedDB:", err));
  }

  // Sync to backend file system
  if (typeof window !== "undefined" && typeof FormData !== "undefined") {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("path", cleanPath);
    fetch("/api/upload-image", {
      method: "POST",
      body: formData
    }).then((res) => res.json()).then((data) => {
      if (data.success && data.url) {
        console.log("Image synced with backend disk storage:", data.url);
      }
    }).catch((err) => {
      console.warn("Backend image sync notice:", err);
    });
  }

  if (typeof window !== "undefined") {
    setTimeout(() => {
      window.dispatchEvent(new CustomEvent("bmm-images-ready"));
    }, 50);
  }

  return cleanPath;
}

export function resolveImageUrl(path) {
  if (!path || typeof path !== "string" || path.trim() === "") {
    return DEFAULT_FALLBACK_IMAGE;
  }
  const clean = path.trim();

  // If unsplash was stored in DB, sanitize to local upload path
  if (clean.includes("unsplash.com")) {
    if (clean.includes("photo-1542816417") || clean.includes("campus")) return LOCAL_FALLBACK_IMAGES.campus;
    if (clean.includes("photo-1585036156") || clean.includes("classroom")) return LOCAL_FALLBACK_IMAGES.classroom;
    if (clean.includes("photo-1609599006") || clean.includes("curriculum") || clean.includes("book")) return LOCAL_FALLBACK_IMAGES.curriculum;
    if (clean.includes("photo-1577495508") || clean.includes("teacher")) return LOCAL_FALLBACK_IMAGES.teachers;
    return DEFAULT_FALLBACK_IMAGE;
  }

  if (clean.startsWith("blob:") || clean.startsWith("data:")) {
    return clean;
  }

  // In-memory or IndexedDB active blob session
  if (blobUrlRegistry.has(clean)) {
    return blobUrlRegistry.get(clean);
  }
  const withSlash = clean.startsWith("/") ? clean : `/${clean}`;
  if (blobUrlRegistry.has(withSlash)) {
    return blobUrlRegistry.get(withSlash);
  }
  const withoutSlash = clean.startsWith("/") ? clean.substring(1) : clean;
  if (blobUrlRegistry.has(withoutSlash)) {
    return blobUrlRegistry.get(withoutSlash);
  }

  return clean;
}

const imageStorageExports = {
  LOCAL_FALLBACK_IMAGES,
  PRESET_UPLOAD_PHOTOS,
  PRESET_TEACHER_PHOTOS,
  DEFAULT_FALLBACK_IMAGE,
  initImageStorage,
  sanitizeFileName,
  saveImageToUploadFolder,
  resolveImageUrl
};

export default imageStorageExports;
